import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsDataProvider } from '@mui/x-charts/ChartsDataProvider';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LineHighlightPlot, LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import type { BarSeriesType, CurveType, LineSeriesType } from '@mui/x-charts/models';
import { type FC, useCallback, useMemo, useState } from 'react';
import type { SalesTotal } from '../../data/sales-total';
import type { Store } from '../../data/store';
import { shortMonthNames } from '../../helpers/months';
import { SegmentedToggle } from '../segmented-toggle';
import { thisYearColor, thisYearEstimatesColor } from './constants';
import { formatDisplayValue, getDisplayValue, getMarginPercent } from './helpers';
import {
  AverageOrderValueSummary,
  MarginSummary,
  SalesSummary,
  TransactionsSummary,
} from './summaries';
import type { DisplayType } from './types';
import { WidgetContainer } from './widget-container';

const createSalesData = (totals: SalesTotal[], displayType: DisplayType) => {
  return totals.map((total) => getDisplayValue(total, displayType));
};

type SalesDisplayProperties = {
  store: Store;
  displayType: DisplayType;
};

type MarginUnit = 'dollar' | 'percent';

export const StoreSalesStatsDisplay: FC<SalesDisplayProperties> = ({ store, displayType }) => {
  const [marginUnit, setMarginUnit] = useState<MarginUnit>('dollar');
  // Margin percentage only makes sense for the Gross Margin widget. In that mode
  // we drop the bars (you can't stack percentages) and plot lines only.
  const showPercent = displayType === 'Gross Margin' && marginUnit === 'percent';

  const { historicalYears, thisYearSales } = useMemo(() => {
    const historical = store.sales_stats.historical;
    const thisYearSales = historical.at(-1);
    if (!thisYearSales) {
      throw 'Missing this year sales data.';
    }

    // Get up to 4 previous years, excluding current year
    const historicalYears = historical.slice(-5, -1).reverse();

    return { historicalYears, thisYearSales };
  }, [store]);

  const valueFormatter = useCallback(
    (value: number | null) => formatDisplayValue(value, displayType, showPercent),
    [displayType, showPercent]
  );

  // Current-year series: a percentage line in percent mode, otherwise the
  // stacked actual + estimate bars.
  const currentYearSeries = useMemo(() => {
    if (showPercent) {
      return [
        {
          type: 'line' as const,
          label: `${thisYearSales.year}`,
          data: thisYearSales.monthly.map(getMarginPercent),
          id: 'thisYear',
          curve: 'linear' as CurveType,
          color: thisYearColor,
          showMark: true,
          labelMarkType: 'square',
          valueFormatter,
        },
      ] satisfies LineSeriesType[];
    }

    const thisYearData = createSalesData(thisYearSales.monthly, displayType);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const salesYear = thisYearSales.year;

    const thisYearEstimatesData = createSalesData(
      store.sales_stats.current_year_estimates.monthly,
      displayType
    ).map((value, index) => {
      // Determine if this month is in the past
      // If sales year is less than current year, all months are in the past
      // If sales year equals current year, months before current month are in the past
      const isPastMonth =
        salesYear < currentYear || (salesYear === currentYear && index < currentMonth);

      if (isPastMonth) {
        return 0;
      }

      // For the current month, deduct the actual value from the estimates to make the bar stacking work correctly
      if (salesYear === currentYear && index === currentMonth) {
        if (displayType === 'Average Order Value') {
          // Can't stack averages, so suppress the estimate when actual data exists.
          // But when there's no actual data yet, show the full estimate to avoid an empty bar.
          return thisYearData[index] === 0 ? value : 0;
        }

        return Math.max(0, value - thisYearData[index]);
      }

      return value;
    });

    return [
      {
        type: 'bar' as const,
        label: `${thisYearSales?.year}`,
        data: thisYearData,
        id: 'thisYear',
        color: thisYearColor,
        valueFormatter,
        stack: 'currentYear',
      },
      {
        type: 'bar' as const,
        label: `${thisYearSales?.year} Est.`,
        data: thisYearEstimatesData,
        id: 'thisYearEstimates',
        color: thisYearEstimatesColor,
        valueFormatter: (value: number | null, { dataIndex }) => {
          // For the current month, re-add the current value to the estimates to make the tooltip content correct
          if (salesYear === currentYear && dataIndex === currentMonth) {
            if (displayType === 'Average Order Value') {
              // When showing full estimate (no actual data yet), display the estimate value.
              // When actual data exists, the estimate contribution is suppressed — show N/A.
              return thisYearData[dataIndex] === 0 ? valueFormatter(value) : 'N/A';
            }

            return valueFormatter((value || 0) + thisYearData[dataIndex]);
          }

          return valueFormatter(value);
        },
        stack: 'currentYear',
      },
    ] satisfies BarSeriesType[];
  }, [store, thisYearSales, displayType, valueFormatter, showPercent]);

  const historicalSeries = useMemo(() => {
    return historicalYears.map((yearData, index) => {
      const opacity = 1.0 - index * 0.2;

      return {
        type: 'line' as const,
        label: `${yearData.year}`,
        data: showPercent
          ? yearData.monthly.map(getMarginPercent)
          : createSalesData(yearData.monthly, displayType),
        id: `year${yearData.year}`,
        curve: 'linear' as CurveType,
        color: `rgba(59, 130, 246, ${opacity})`,
        showMark: true,
        labelMarkType: 'square',
        valueFormatter,
      };
    }) satisfies LineSeriesType[];
  }, [historicalYears, displayType, valueFormatter, showPercent]);

  const yAxisBounds = useMemo(() => {
    const values = [...currentYearSeries, ...historicalSeries]
      .flatMap((s) => s.data)
      .filter((value): value is number => value !== null && !Number.isNaN(value));
    const max = values.length > 0 ? Math.max(...values) : 0;

    if (!showPercent) {
      // Dollars: anchor at 0, round the top up to a whole tick value as before.
      return { min: 0, max: Math.ceil(max * 1.1) };
    }

    // Margins cluster in a narrow band, so a 0-based axis flattens the variation.
    // Zoom to the data range with padding, snapped to whole percentage points.
    const min = values.length > 0 ? Math.min(...values) : 0;
    const pad = Math.max((max - min) * 0.25, 0.01);

    return {
      min: Math.max(0, Math.floor((min - pad) * 100) / 100),
      max: Math.ceil((max + pad) * 100) / 100,
    };
  }, [currentYearSeries, historicalSeries, showPercent]);

  return (
    <WidgetContainer
      title={displayType}
      action={
        displayType === 'Gross Margin' ? (
          <SegmentedToggle<MarginUnit>
            value={marginUnit}
            onChange={setMarginUnit}
            className="[&_button]:px-4 [&_button]:py-1 [&_button]:leading-5"
            options={[
              { value: 'dollar', label: '$' },
              { value: 'percent', label: '%' },
            ]}
          />
        ) : undefined
      }
    >
      {displayType === 'Gross Margin' && (
        <MarginSummary
          lastImported={store.sales_stats.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.sales_stats.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Sales' && (
        <SalesSummary
          lastImported={store.sales_stats.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.sales_stats.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Transactions' && (
        <TransactionsSummary
          lastImported={store.sales_stats.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.sales_stats.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Average Order Value' && (
        <AverageOrderValueSummary
          lastImported={store.sales_stats.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.sales_stats.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      <ChartsDataProvider
        width={500}
        height={300}
        series={[...currentYearSeries, ...historicalSeries]}
        xAxis={[{ data: shortMonthNames, scaleType: 'band' }]}
        yAxis={[
          {
            scaleType: 'linear',
            min: yAxisBounds.min,
            max: yAxisBounds.max,
            tickNumber: 5,
            valueFormatter: (value: number | null) => {
              if (value === null || value === 0) {
                return showPercent ? '0%' : '0';
              }

              return valueFormatter(value);
            },
            width: 80,
          },
        ]}
        margin={{ left: -20, right: 0, top: 20, bottom: 10 }}
      >
        <ChartsSurface>
          <BarPlot />
          <LinePlot />
          <MarkPlot slotProps={{ mark: { shape: 'circle' } }} />
          <LineHighlightPlot />
          <ChartsTooltip />
          <ChartsXAxis />
          <ChartsYAxis />
          <ChartsGrid
            horizontal
            classes={{
              horizontalLine: 'opacity-50',
            }}
          />
        </ChartsSurface>
        <ChartsLegend
          direction="horizontal"
          slotProps={{
            legend: {
              sx: {
                justifyContent: 'flex-end',
                marginRight: '0',
                fontSize: '1rem',
                fontWeight: 'Normal',
                '& .MuiChartsLegend-mark': {
                  marginRight: '1px',
                  transform: 'scale(1.3)',
                },
                '& .MuiChartsLegend-series': {
                  gap: '6px',
                },
              },
            },
          }}
        />
      </ChartsDataProvider>
    </WidgetContainer>
  );
};
